import { Link } from 'react-router-dom';

const features = [
  {
    category: 'Email Marketing',
    items: [
      { title: 'Drag & Drop Builder', desc: 'Intuitive email builder with pre-designed blocks and templates.' },
      { title: 'A/B Testing', desc: 'Test subject lines, content, and send times to optimize performance.' },
      { title: 'Scheduling', desc: 'Schedule campaigns for optimal delivery times across time zones.' },
      { title: 'Personalization', desc: 'Dynamic content with {{first_name}}, {{company}}, and custom variables.' },
    ],
  },
  {
    category: 'AI-Powered Features',
    items: [
      { title: 'AI Email Writer', desc: 'Generate complete emails from a brief description.' },
      { title: 'Subject Line Generator', desc: 'Get 5+ high-converting subject line options instantly.' },
      { title: 'AI Workflow Builder', desc: 'Create complex automations with natural language.' },
      { title: 'Campaign Optimization', desc: 'AI analyzes performance and suggests improvements.' },
      { title: 'Send-Time Optimization', desc: 'AI recommends the best time to send for maximum engagement.' },
      { title: 'Business Understanding', desc: 'AI learns your business to personalize all outputs.' },
    ],
  },
  {
    category: 'Contact Management',
    items: [
      { title: 'Smart Segments', desc: 'Dynamic segments based on behavior, engagement, and custom fields.' },
      { title: 'Tags & Lists', desc: 'Organize contacts with tags, lists, and custom fields.' },
      { title: 'CSV Import', desc: 'Bulk import contacts with automatic deduplication.' },
      { title: 'GDPR Tools', desc: 'Consent management, data export, and deletion requests.' },
    ],
  },
  {
    category: 'Automation',
    items: [
      { title: 'Visual Workflow Builder', desc: 'Build complex automations with drag-and-drop nodes.' },
      { title: 'Pre-built Templates', desc: 'Onboarding, abandoned cart, re-engagement, and more.' },
      { title: 'Trigger-Based Actions', desc: 'Trigger workflows on signup, purchase, tags, dates, or webhooks.' },
      { title: 'Conditional Logic', desc: 'Branch workflows based on contact behavior and attributes.' },
    ],
  },
  {
    category: 'Analytics & Reporting',
    items: [
      { title: 'Real-Time Dashboard', desc: 'Monitor opens, clicks, bounces, and unsubscribes in real time.' },
      { title: 'Campaign Reports', desc: 'Detailed performance metrics for every campaign.' },
      { title: 'Engagement Tracking', desc: 'Track individual contact engagement over time.' },
      { title: 'AI Insights', desc: 'AI-powered recommendations to improve performance.' },
    ],
  },
  {
    category: 'Developer Tools',
    items: [
      { title: 'REST API', desc: 'Full API access for campaigns, contacts, and automations.' },
      { title: 'Webhooks', desc: 'Real-time notifications for events and triggers.' },
      { title: 'API Keys', desc: 'Manage API keys with granular permissions.' },
      { title: 'OAuth2', desc: 'Secure OAuth2 integration for third-party apps.' },
    ],
  },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-900">Powerful Features</h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Everything you need to create, send, and optimize email campaigns — powered by AI.</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {features.map((section) => (
            <div key={section.category} className="mb-16 last:mb-0">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{section.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {section.items.map((item) => (
                  <div key={item.title} className="p-6 bg-white border border-gray-200 rounded-xl hover:shadow-lg transition-shadow">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600 mt-2">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold">Ready to experience these features?</h2>
          <p className="mt-2 text-blue-100">Start your free trial today. No credit card required.</p>
          <Link to="/login" className="inline-block mt-6 px-8 py-3 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-colors">
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
  );
}
